import { IAdditionalProductInfoMap } from "../models/product/IAdditionalProductInfoMap";
import ProductInfo from "../models/product/ProductInfo";
import { ProductUtils } from "../utils/ProductUtils";
import ValueUtils from "../utils/ValueUtils";
import { IProductProvider } from "./IProductProvider";
import { IAdditionalProductsWebProviderParams } from "./ProductProviderWeb";

export class ProductProvider {
    public provider: IProductProvider;
    private additionalParams: IAdditionalProductsWebProviderParams;
    private productInfosPromise: Promise<ProductInfo[]>;
    private productInfos: ProductInfo[] = null;

    private ownershipInfoPromise: Promise<{[key: string]: boolean}>;
    private productAdditionalInfoMap: IAdditionalProductInfoMap = {};
    
    constructor(provider: IProductProvider, additionalParams?: IAdditionalProductsWebProviderParams){
        this.provider = provider;
        this.additionalParams = additionalParams;
        
    }
    public getOwnershipInfo() {
        if(this.ownershipInfoPromise) { return this.ownershipInfoPromise; }

        return this.ownershipInfoPromise = this.provider.getOwnershipInfo();
    } 

    public getProductInfos(addSpecialProductResults: boolean, prioritizeLastSignificantDate: boolean = false, lpLinkIdField: string = "externalId"): Promise<ProductInfo[]>{
        if(this.productInfos != null) { return Promise.resolve(this.productInfos); }
        if(this.productInfosPromise != null) { return this.productInfosPromise; }
       
        return this.productInfosPromise = new Promise<ProductInfo[]>((resolve, reject) => {
            if(!this.provider.webUtils.isOnline() && this.additionalParams != null && this.additionalParams.getProductInfosFallback != null){
                this.additionalParams.getProductInfosFallback()
                                     .then(data => resolve(this.convertRawServerDataToProductInfos(data)))
                                     .catch(e => reject(e));
                return;
            }

            this.provider.getRawProductInfos().then(data => {
                let productInfos = ProductInfo.convertRawProducts(data, prioritizeLastSignificantDate);
    
                if(addSpecialProductResults){
                    productInfos.push(ProductInfo.createSpecialLivebookProduct());
                    productInfos.push(ProductInfo.createSpecialLivevideoProduct());
                    productInfos.push(ProductInfo.createSpecialLiveprojectProduct());
                    productInfos.push(ProductInfo.createSpecialMeapProduct());
                }
                    
                resolve(productInfos = ProductUtils.upgradeProductInfoPaths(productInfos, this.provider.webUtils.configuration, lpLinkIdField));
            }).catch(e => reject(e));
        });
    }

    public getAdditionalProductInfoMap(productIds: number[]): Promise<IAdditionalProductInfoMap> {
        if(!productIds || !productIds.length) { return Promise.resolve({}); }
        
        const existingProductsWithAdditionalInfo: IAdditionalProductInfoMap = {};
        const missingIds: number[] = [];
        for(const productId of productIds){
            if(this.productAdditionalInfoMap[productId]){
                existingProductsWithAdditionalInfo[productId] = this.productAdditionalInfoMap[productId];
            }
            else {
                if(ValueUtils.isNumber(productId)){
                    missingIds.push(productId);
                }
                else {
                    existingProductsWithAdditionalInfo[productId] = null;
                }
            }
        }

        if(missingIds.length == 0){
            return Promise.resolve(existingProductsWithAdditionalInfo);
        }

        return new Promise<IAdditionalProductInfoMap>((resolve, reject) => {
            this.provider.getAdditionalProductInformation(missingIds).then(map => {
                if(map){
                    for(const item of map){
                        this.productAdditionalInfoMap[item.id] = item;
                        existingProductsWithAdditionalInfo[item.id] = item;
                    }
                    
                    resolve(existingProductsWithAdditionalInfo);
                }
                else {
                    reject();
                }
            }).catch(e => reject(e));
        });
    }

    private convertRawServerDataToProductInfos(data){
        const productInfos = ProductInfo.convertRawProducts(data);

        productInfos.push(ProductInfo.createSpecialLivebookProduct());
        productInfos.push(ProductInfo.createSpecialLivevideoProduct());
        productInfos.push(ProductInfo.createSpecialLiveprojectProduct());
        productInfos.push(ProductInfo.createSpecialMeapProduct());

        return ProductUtils.upgradeProductInfoPaths(productInfos, this.provider.webUtils.configuration);
    }
}